
// Note: The indent rule is disabled due to a known ESLint bug with complex JSX patterns
// that causes "Maximum call stack size exceeded" errors. The file is formatted with Prettier.
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  Customized,
} from 'recharts';
import {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent';
import { ActivityNames } from '../../models/CaseTypes';
import { getActivityColor, getActivityLabel } from '../../utils/caseUtils';
import { ToasterNotice } from '../common/ToasterNotice';

// Timeline segment data for CaseTimelineChart
export interface TimelineSegmentData {
  segmentType: string;        // Activity type (e.g., "Exposure")
  startMin: number;           // Start time in minutes from case start
  endMin: number;             // End time in minutes from case start
  duration: number;           // Duration in minutes
  displayDuration: string;    // Formatted (e.g., "15m" or "1h 30m")
  color: string;              // CSS variable reference
  swimLane: number;           // Row index (0-based)
  // Video playback data
  videoIsValid: boolean;
  videoStartSec: number | null;
  videoEndSec: number | null;
  signedUrl: string;
}

export interface CaseTimelineChartProps {
  segments: TimelineSegmentData[];
  totalTime: string;            // Formatted total time (e.g., "7h 12m")
  onSegmentClick?: (segment: TimelineSegmentData) => void;
}

// Legend items in display order
// Includes both v1.0 and v1.2 segment names since data may contain either
const LEGEND_ITEMS = [
  ActivityNames.EXPOSURE,
  ActivityNames.INTRAOP_MEASUREMENT,
  ActivityNames.CANNULATION_SCREW_PLACEMENT, // v1.0 combined
  ActivityNames.ANATOMY_LOCALIZATION,        // v1.2 split
  ActivityNames.CANNULATION,                 // v1.2 split
  ActivityNames.SCREW_PLACEMENT,             // v1.2 split
  ActivityNames.POST_CANNULATION,
  ActivityNames.CLOSURE,
  ActivityNames.DECOMPRESSION_ALIGNMENT,
].map(activity => ({
  activity,
  name: getActivityLabel(activity),
  color: getActivityColor(activity),
}));

// Breakpoint for mobile vs desktop interactions
const MOBILE_BREAKPOINT = 744;

// Breakpoint for side-by-side layout (lg in Tailwind)
const SIDE_BY_SIDE_BREAKPOINT = 1024;

// Minimum lanes when charts are side by side
const MIN_LANES_SIDE_BY_SIDE = 8;

// Minimum width per 30-minute column in pixels
const MIN_COLUMN_WIDTH = 96;

// Generate a stable key for segment identity comparison
const getSegmentKey = (segment: TimelineSegmentData): string =>
  `${segment.segmentType}-${segment.startMin}-${segment.swimLane}`;

/**
 * CaseTimelineChart - A horizontal Gantt-style timeline chart
 * showing surgical segments across swim lanes.
 */
export function CaseTimelineChart({
  segments,
  totalTime,
  onSegmentClick,
}: CaseTimelineChartProps): React.JSX.Element {
  const [selectedSegment, setSelectedSegment] = useState<TimelineSegmentData | null>(null);
  const [hoveredSegment, setHoveredSegment] = useState<TimelineSegmentData | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
  });
  const [isSideBySide, setIsSideBySide] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= SIDE_BY_SIDE_BREAKPOINT;
  });
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      setIsSideBySide(window.innerWidth >= SIDE_BY_SIDE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return (): void => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate min/max time for X-axis domain
  // Min is rounded down to nearest 30 min, max is rounded up
  const { minTimeMinutes, maxTimeMinutes } = useMemo(() => {
    if (segments.length === 0) return { minTimeMinutes: 0, maxTimeMinutes: 120 };
    const min = Math.min(...segments.map((s) => s.startMin));
    const max = Math.max(...segments.map((s) => s.endMin));
    return {
      minTimeMinutes: Math.floor(min / 30) * 30,
      maxTimeMinutes: Math.ceil(max / 30) * 30,
    };
  }, [segments]);

  // Calculate number of 30-minute columns
  const columnCount = Math.ceil((maxTimeMinutes - minTimeMinutes) / 30) || 1;

  // Calculate minimum chart width based on column count
  const minChartWidth = columnCount * MIN_COLUMN_WIDTH;

  // Generate X-axis ticks at 30-minute intervals
  const xAxisTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let i = minTimeMinutes; i <= maxTimeMinutes; i += 30) {
      ticks.push(i);
    }
    return ticks;
  }, [minTimeMinutes, maxTimeMinutes]);

  // Format X-axis tick values: "0h", "30m", "1h", "1h 30m", etc.
  const formatXAxisTick = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours}h`;
    }
    if (hours === 0) {
      return `${mins}m`;
    }
    return `${hours}h ${mins}m`;
  };

  // Calculate active swim lanes (only lanes that have segments)
  // and create a mapping from original lane index to compressed index
  // When side by side, enforce minimum of 8 lanes with empty lanes at top
  const { displayLaneCount, laneMapping } = useMemo(() => {
    // Get unique lane indices that have segments
    const activeLanes = [...new Set(segments.map(s => s.swimLane))].sort((a, b) => a - b);
    const actualLaneCount = activeLanes.length || 1;

    // Apply minimum lanes when side by side
    const minLanes = isSideBySide ? MIN_LANES_SIDE_BY_SIDE : 1;
    const totalLanes = Math.max(actualLaneCount, minLanes);

    // Create mapping from original lane index to compressed index
    const mapping: Record<number, number> = {};
    activeLanes.forEach((originalLane, compressedIndex) => {
      mapping[originalLane] = compressedIndex; // Data lanes start at index 0 (bottom)
    });

    return {
      displayLaneCount: totalLanes,
      laneMapping: mapping,
    };
  }, [segments, isSideBySide]);

  // Create dummy data for the BarChart Y-axis categories
  const swimLaneData = useMemo(() => {
    return Array.from({ length: displayLaneCount }, (_, i) => ({
      lane: i,
      placeholder: 0,
    }));
  }, [displayLaneCount]);

  // Sort segments chronologically for navigation
  const sortedSegments = useMemo(() => {
    return [...segments].sort((a, b) => a.startMin - b.startMin);
  }, [segments]);

  // Find index of selected segment in sorted list
  const selectedSegmentIndex = useMemo(() => {
    if (!selectedSegment) return -1;
    const selectedKey = getSegmentKey(selectedSegment);
    return sortedSegments.findIndex(s => getSegmentKey(s) === selectedKey);
  }, [selectedSegment, sortedSegments]);

  // Navigation handlers for ToasterNotice
  const handlePrevSegment = useCallback((): void => {
    if (selectedSegmentIndex > 0) {
      setSelectedSegment(sortedSegments[selectedSegmentIndex - 1]);
    }
  }, [selectedSegmentIndex, sortedSegments]);

  const handleNextSegment = useCallback((): void => {
    if (selectedSegmentIndex < sortedSegments.length - 1) {
      setSelectedSegment(sortedSegments[selectedSegmentIndex + 1]);
    }
  }, [selectedSegmentIndex, sortedSegments]);

  // Handle Watch button click in mobile drawer
  const handleWatchClick = (): void => {
    if (selectedSegment) {
      onSegmentClick?.(selectedSegment);
    }
  };

  // Handle segment click
  const handleSegmentClick = useCallback((segment: TimelineSegmentData): void => {
    const segmentKey = getSegmentKey(segment);
    const isCurrentlySelected = selectedSegment !== null &&
      getSegmentKey(selectedSegment) === segmentKey;

    if (isCurrentlySelected) {
      setSelectedSegment(null);
    } else {
      setSelectedSegment(segment);
      if (!isMobile) {
        onSegmentClick?.(segment);
      }
    }
  }, [selectedSegment, isMobile, onSegmentClick]);

  // Custom tooltip for desktop hover
  const CustomTooltip = ({
    active,
  }: TooltipProps<ValueType, NameType>): React.JSX.Element | null => {
    if (!active || !hoveredSegment) return null;

    return (
      <div
        className="rounded-[var(--radius)] border border-border p-3 shadow-elevation-sm"
        style={{
          backgroundColor: 'var(--popover)',
          color: 'var(--popover-foreground)',
        }}
      >
        <p className="font-semibold text-sm mb-1">{getActivityLabel(hoveredSegment.segmentType)}</p>
        <p className="text-sm font-semibold" style={{ color: hoveredSegment.color }}>
          {hoveredSegment.displayDuration}
        </p>
      </div>
    );
  };

  // Custom legend renderer
  const renderLegend = (): React.JSX.Element => {
    // Filter legend items to only show those that have segments in the data
    const segmentTypes = new Set(segments.map(s => s.segmentType));
    const visibleLegendItems = LEGEND_ITEMS.filter(item => segmentTypes.has(item.activity));

    return (
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-2">
        {visibleLegendItems.map((item, index) => (
          <div key={index} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-xs text-medium-emphasis-font whitespace-nowrap">
              {item.name}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Custom renderer for timeline segments
  const renderTimelineSegments = (props: {
    xAxisMap?: Record<string, { scale: (value: number) => number }>;
    offset?: { top: number; bottom: number; left: number; right: number };
    height?: number;
  }): React.JSX.Element | null => {
    const { xAxisMap, offset, height } = props;
    if (!xAxisMap || !offset || !height) return null;

    const xAxis = Object.values(xAxisMap)[0];
    if (!xAxis) return null;

    // Calculate swim lane dimensions from chart height
    const chartTop = offset.top;
    const chartBottom = height - offset.bottom;
    const chartHeight = chartBottom - chartTop;
    const bandHeight = chartHeight / displayLaneCount;
    const barHeight = bandHeight; // Full lane height, no gaps between lanes
    const barOffset = 0;

    // Get chart width for horizontal lines
    const chartLeft = offset.left;
    const chartRight = xAxis.scale(maxTimeMinutes);

    // Create horizontal grid lines between lanes (at lane boundaries)
    const horizontalLines = [];
    for (let i = 0; i <= displayLaneCount; i++) {
      const lineY = chartTop + i * bandHeight;
      horizontalLines.push(
        <line
          key={`h-grid-${i}`}
          x1={chartLeft}
          y1={lineY}
          x2={chartRight}
          y2={lineY}
          stroke="var(--border)"
          strokeWidth={1}
        />
      );
    }

    return (
      <g>
        {/* Custom horizontal grid lines between lanes */}
        {horizontalLines}

        {/* Segment bars */}
        {segments.map((segment, index) => {
          const x = xAxis.scale(segment.startMin);
          const width = xAxis.scale(segment.endMin) - x;
          // Map original swim lane to compressed lane index
          const compressedLane = laneMapping[segment.swimLane] ?? 0;
          // Swim lanes are from bottom (0) to top (displayLaneCount-1)
          // In SVG, Y increases downward, so we need to invert
          const laneY = chartTop + (displayLaneCount - 1 - compressedLane) * bandHeight + barOffset;
          const isSelected = selectedSegment !== null && getSegmentKey(selectedSegment) === getSegmentKey(segment);
          const isHovered = hoveredSegment !== null && getSegmentKey(hoveredSegment) === getSegmentKey(segment);

          // Common event handlers
          const eventHandlers = {
            onClick: (e: React.MouseEvent): void => {
              e.stopPropagation();
              handleSegmentClick(segment);
            },
            onMouseEnter: (): void => {
              if (!isMobile) {
                setHoveredSegment(segment);
              }
            },
            onMouseLeave: (): void => {
              if (!isMobile) {
                setHoveredSegment(null);
              }
            },
          };

          // Render segments as bars
          return (
            <rect
              key={`segment-${index}`}
              x={x}
              y={laneY}
              width={Math.max(width, 4)} // Minimum width for visibility
              height={barHeight}
              fill={segment.color}
              stroke="var(--bar-stroke)"
              strokeWidth={1}
              style={{
                cursor: 'pointer',
                opacity: isHovered && !isSelected ? 0.8 : 1,
              }}
              {...eventHandlers}
            />
          );
        })}
      </g>
    );
  };

  return (
    <div
      className="rounded-[var(--radius-card)] border border-border p-4 flex flex-col h-full"
      style={{ backgroundColor: 'var(--card)' }}
    >
      {/* Header Section - Responsive layout */}
      <div className="mb-4 flex flex-col md:flex-row md:justify-between md:items-start">
        <h3 className="text-lg font-semibold text-high-emphasis-font mb-2 md:mb-0">
          Case Timeline
        </h3>
        {/* Mobile layout: time above label */}
        <div className="flex flex-col md:hidden">
          <span className="text-2xl font-bold text-high-emphasis-font">
            {totalTime || '0h 0m'}
          </span>
          <span className="text-sm text-medium-emphasis-font">
            Total Navigated Time
          </span>
        </div>
        {/* Desktop layout: label and time on same row, right-aligned */}
        <div className="hidden md:flex md:flex-row md:items-baseline md:gap-2">
          <span className="text-sm text-medium-emphasis-font">
            Total Navigated Time
          </span>
          <span className="text-2xl font-bold text-high-emphasis-font">
            {totalTime || '0h 0m'}
          </span>
        </div>
      </div>

      {/* Spacer to push chart to bottom */}
      <div className="flex-1" />

      {/* Chart Container - fixed height based on number of active lanes, horizontally scrollable */}
      <div
        ref={chartContainerRef}
        className="overflow-x-auto overflow-y-hidden"
        style={{ height: Math.max(100, displayLaneCount * 32 + 45) }}
      >
        <div style={{ minWidth: minChartWidth, height: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={swimLaneData}
              layout="vertical"
              margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="0"
                horizontal={false}
                vertical={true}
                stroke="var(--border)"
              />

              {/* X-Axis: Timeline in minutes */}
              <XAxis
                type="number"
                domain={[minTimeMinutes, maxTimeMinutes]}
                ticks={xAxisTicks}
                tickFormatter={formatXAxisTick}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
                tick={{ fontSize: 11, fill: 'var(--medium-emphasis-font)' }}
              />

            {/* Y-Axis: Swim lanes (hidden labels per requirements) */}
            <YAxis
              type="category"
              dataKey="lane"
              hide={true}
              axisLine={{ stroke: 'var(--border)' }}
            />

            {/* Tooltip: Only shown on desktop hover */}
            {!isMobile && hoveredSegment && (
              <Tooltip
                content={<CustomTooltip />}
                cursor={false}
                position={{ x: 0, y: 0 }}
                wrapperStyle={{ visibility: 'visible' }}
              />
            )}

            {/* Custom timeline segments renderer */}
            <Customized component={renderTimelineSegments} />
          </BarChart>
        </ResponsiveContainer>
        </div>
      </div>

      {/* Legend - Always visible */}
      {renderLegend()}

      {/* Mobile Toaster Notice */}
      {isMobile && selectedSegment && (
        <ToasterNotice
          title={getActivityLabel(selectedSegment.segmentType)}
          content={
            <span className="text-sm" style={{ color: selectedSegment.color }}>
              {selectedSegment.displayDuration}
            </span>
          }
          onWatch={handleWatchClick}
          onClose={() => setSelectedSegment(null)}
          onPrev={handlePrevSegment}
          onNext={handleNextSegment}
          hasPrev={selectedSegmentIndex > 0}
          hasNext={selectedSegmentIndex < sortedSegments.length - 1}
        />
      )}
    </div>
  );
}

export default CaseTimelineChart;
